import React, { useEffect } from 'react'
import { Store } from 'types'
import Row from 'antd/lib/row'
import Col from 'antd/lib/col'
import Table from 'antd/lib/table'
import Button from 'antd/lib/button'
import Result from 'antd/lib/result'
import styled from '@emotion/styled'
import { formatMoney } from 'packages/@slerp/designer-rework/shop/components/Cart/utils'
import { useSession } from '@slerp/accounts'
import { useLazyQuery } from '@apollo/client'
import { CreditNote, Pagination } from './types'
import { DownloadIcon } from 'packages/@slerp/assets'
import { getBillingPeriod, getCreditNoteParams, formatDate } from './utils'
import { SorterTitle, SortDirectionOptions } from '../../Common'
import {
  QUERY_MERCHANT_CREDIT_NOTES,
  QUERY_STORE_CREDIT_NOTES
} from '../SettingsQueries'

const CreditNotes = ({ store }: { store?: Store }) => {
  const { merchant, user } = useSession()

  const [creditNotes, setCreditNotes] = React.useState<CreditNote[]>([])
  const [pagination, setPagination] = React.useState<Pagination | null>()
  const [sortDirection, setSortDirection] =
    React.useState<SortDirectionOptions>(SortDirectionOptions.descend)

  const query = store ? QUERY_STORE_CREDIT_NOTES : QUERY_MERCHANT_CREDIT_NOTES

  const [fetchCreditNotes, { loading }] = useLazyQuery(query, {
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      const creditNoteResult = store
        ? data.listStoreCreditNotes.credit_notes
        : data.listCreditNotes.credit_notes
      setCreditNotes(creditNoteResult)

      if (creditNoteResult.length < 10) {
        setPagination(null)
        return
      }

      setPagination(
        store
          ? data.listStoreCreditNotes.pagination
          : data.listCreditNotes.pagination
      )
    }
  })

  const [fetchMoreCreditNotes, { loading: loadingMore }] = useLazyQuery(query, {
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      const creditNoteResult = store
        ? data.listStoreCreditNotes.credit_notes
        : data.listCreditNotes.credit_notes
      setCreditNotes((prev) =>
        [...prev, ...creditNoteResult].filter((value) => !!value)
      )
      setPagination(
        store
          ? data.listStoreCreditNotes.pagination
          : data.listCreditNotes.pagination
      )
    }
  })

  useEffect(() => {
    fetchCreditNotes({
      variables: getCreditNoteParams({
        storeId: store?.id ?? '',
        merchantId: merchant?.id ?? '',
        sortBy: 'CREDIT_NOTE_NUMBER',
        sortDirection
      })
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortDirection])

  const COLUMNS = [
    {
      title: 'Credit Note #',
      dataIndex: 'creditNoteNumber',
      key: 'creditNoteNumber',
      render: (reference: string) => <CellSpan>#{reference}</CellSpan>
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      render: (amount: string) => (
        <CellSpan>{formatMoney(parseFloat(amount))}</CellSpan>
      )
    },
    {
      title: 'Billing Period',
      render: (record: CreditNote) => {
        return (
          <CellSpan>
            {getBillingPeriod(
              record.billingPeriodStart,
              record.billingPeriodEnd
            )}
          </CellSpan>
        )
      }
    },
    {
      title: (
        <SorterTitle
          onClick={() =>
            sortDirection === SortDirectionOptions.ascend
              ? setSortDirection(SortDirectionOptions.descend)
              : setSortDirection(SortDirectionOptions.ascend)
          }
          sortDirection={sortDirection}
        >
          Sent
        </SorterTitle>
      ),
      dataIndex: 'date',
      render: (date: string) => <CellSpan>{formatDate(date)}</CellSpan>
    },
    {
      title: (
        <div style={{ textAlign: 'center' }}>
          <span>Action</span>
        </div>
      ),
      dataIndex: 'downloadLink',
      render: (downloadLink: string) => (
        <Row justify='center' align='middle' style={{ marginBottom: 0 }}>
          <Col>
            <a
              href={`${downloadLink}?token=${user?.api_key}`}
              target='_blank'
              rel='noreferrer'
            >
              <StyledButton>
                <DownloadIcon style={{ fontSize: '16px', marginTop: '2px' }} />
              </StyledButton>
            </a>
          </Col>
        </Row>
      )
    }
  ]

  const fetchNext = () => {
    if (!pagination?.after) {
      return
    }

    fetchMoreCreditNotes({
      variables: getCreditNoteParams({
        storeId: store?.id ?? '',
        merchantId: merchant?.id ?? '',
        after: pagination.after,
        sortBy: 'ACCOUNTING_DATE',
        sortDirection
      })
    })
  }

  const isEmptyWithNoFilters = creditNotes.length === 0 && !store && !loading

  if (isEmptyWithNoFilters) {
    return <Result status={404} subTitle='No credit notes available' />
  }

  return (
    <>
      <Table
        data-testid='campaigns-history-table'
        dataSource={creditNotes}
        columns={COLUMNS}
        pagination={false}
        loading={loading || loadingMore}
        rowKey='invoiceReference'
      />
      <Row style={{ marginTop: '10px' }} gutter={10} justify='end'>
        {pagination?.after && (
          <Col>
            <Button onClick={fetchNext}>Load more</Button>
          </Col>
        )}
      </Row>
    </>
  )
}

const CellSpan = styled.span`
  color: #4a4a68;
  font-size: 13px;
  font-weight: 500;
`

const StyledButton = styled(Button)`
  width: 28px;
  height: 28px;
  padding: 0;
  display: flex;
  justify-content: center;
  align-items: center;
`

export default CreditNotes
